import { Check, Close } from "@mui/icons-material";
import { Autocomplete, Box, IconButton, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { TimesheetRow } from "../../../interfaces/time-sheet.interfaces";
import { calcHours, toISO } from "../helpers/calculate-hours.helpers";
import { formatDuration } from "../helpers/formatduration.helpers";
import { getWorkedStatus } from "../helpers/getWorkedStatus.helpers";
import { StatusBadge } from "./StatusBadge";

interface Props {
  row: TimesheetRow;
  formatedTimeSheets: Partial<TimesheetRow>[];
  onSave: (data: { uid: string; startAt: string; endAt: string }) => void;
  onCancel: (uid?: any) => void;
}

const formatDate = (value?: string | null, format = "dd MMM yyyy hh:mm a") => {
  if (!value) return "-";
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toFormat(format) : "-";
};

export default function EditTimeRow({ row, onSave, onCancel, formatedTimeSheets }: Props) {
  const initialJob = formatedTimeSheets.find((j) => j.uid === row.uid) ?? null;
  const initialStart = row.startAt ? DateTime.fromISO(row.startAt).toFormat("HH:mm") : "";
  const initialEnd = row.endAt ? DateTime.fromISO(row.endAt).toFormat("HH:mm") : "";

  const [selectedJob, setSelectedJob] = useState<(typeof formatedTimeSheets)[0] | null>(initialJob);
  const [startAt, setStartAt] = useState(initialStart);
  const [endAt, setEndAt] = useState(initialEnd);

  const totalHours = useMemo(() => calcHours(startAt, endAt), [startAt, endAt]);
  const isInvalidTime = !!startAt && !!endAt && totalHours === null;
  const canSave = !!selectedJob && !!startAt && !!endAt && totalHours !== null;

  const scheduledRange = `${formatDate(row.startDate, "hh:mm a")} - ${formatDate(row.endDate, "hh:mm a")}`;

  const startISO = startAt ? toISO(startAt, selectedJob?.startDate || "") : null;
  const endISO = endAt ? toISO(endAt, selectedJob?.endDate || "") : null;

  const { workedStatus: currentStatus, totalSeconds: currentSeconds } = getWorkedStatus({
    startAt: startISO,
    endAt: endISO,
    startDate: selectedJob?.startDate || "",
    endDate: selectedJob?.endDate || "",
  });

  return (
    <TableRow
      sx={{
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        "& td": {
          borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
          borderTop: "1px solid var(--fx-surface-border, #E2E8F0)",
          py: 1,
          px: 0.5,
        },
      }}
    >
      <TableCell align="center">
        <Autocomplete
          value={selectedJob}
          options={formatedTimeSheets}
          disablePortal
          getOptionLabel={(o) => o.jobAlias || ""}
          isOptionEqualToValue={(a, b) => a.uid === b.uid}
          onChange={(_, val) => setSelectedJob(val)}
          size="small"
          sx={{ minWidth: 100, width: "100%" }}
          renderInput={(params) => <TextField {...params} placeholder="Select job..." sx={fieldSx()} />}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.uid} sx={{ fontSize: 11, fontWeight: 700, px: 1.5, py: 0.75, color: "var(--fx-surface-text, #0F172A)" }}>
              Job: {option.jobAlias}
            </Box>
          )}
        />
      </TableCell>

      <TableCell align="center">
        <Typography sx={{ fontWeight: 800, fontSize: 10, color: "var(--fx-surface-text, #0F172A)" }}>{formatDate(row.startDate, "MMM dd, yyyy")}</Typography>
        <Typography sx={{ fontSize: 9, color: "var(--fx-surface-textsecondary, #64748B)" }}>{scheduledRange}</Typography>
      </TableCell>

      <TableCell align="center">
        <TextField type="time" size="small" value={startAt} onChange={(e) => setStartAt(e.target.value)} sx={{ width: 90, ...fieldSx() }} />
      </TableCell>

      <TableCell align="center">
        <TextField type="time" size="small" value={endAt} onChange={(e) => setEndAt(e.target.value)} sx={{ width: 90, ...fieldSx(isInvalidTime) }} />
      </TableCell>

      <TableCell align="center">
        <StatusBadge status={currentStatus} />
      </TableCell>

      <TableCell align="center">
        <Typography sx={{ fontWeight: 800, fontSize: 11 }}>{formatDuration(currentSeconds)}</Typography>
      </TableCell>

      <TableCell align="right">
        <Box display="flex" justifyContent="flex-end" gap={0.5}>
          <IconButton
            size="small"
            disabled={!canSave}
            onClick={() =>
              canSave &&
              onSave({
                uid: row.uid,
                startAt: toISO(startAt, selectedJob?.startDate || ""),
                endAt: toISO(endAt, selectedJob?.endDate || ""),
              })
            }
            sx={{
              bgcolor: canSave ? "var(--fx-colors-success-100, #D1FAE5)" : "var(--fx-surface-sunken, #F8FAFC)",
              color: canSave ? "#16a34a" : "#cbd5e1",
              "&:hover": { bgcolor: canSave ? "#bbf7d0" : "var(--fx-surface-sunken, #F8FAFC)" },
            }}
          >
            <Check sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onCancel(row.uid)}
            sx={{
              bgcolor: "var(--fx-colors-danger-50, #FEF2F2)",
              color: "#ef4444",
              "&:hover": { bgcolor: "var(--fx-colors-danger-100, #FEE2E2)" },
            }}
          >
            <Close sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
}

const fieldSx = (isError = false) => ({
  "& .MuiOutlinedInput-root": {
    fontSize: 11,
    borderRadius: 1.5,
    bgcolor: "var(--fx-surface-base, #FFFFFF)",
    "& fieldset": {
      borderColor: isError ? "#ef4444" : "var(--fx-surface-border, #E2E8F0)",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: isError ? "#ef4444" : "var(--fx-surface-borderstrong, #CBD5E1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: isError ? "#ef4444" : "var(--fx-surface-texttertiary, #94A3B8)",
      borderWidth: "1px",
    },
  },
});
